import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LOADING_CIRCLE } from 'common/utils/constants';
import { Status, BUTTON_LOADING_CIRCLE } from 'common/utils/constants';
import ScrollReactTable from 'components/shared/ScrollReactTable/ScrollReactTable';
import Modal from 'components/shared/Modal/Modal';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import { IconLoader } from 'common/IconLoader';
import { createApiPolling, isObjectEmpty, productTypeStringToDisplayStringForSwUpdate } from 'common/utils/helpers';
import classnames from 'classnames';
import { getDefaultSwVersions } from 'apis/swManagerApis/GetDefaultSwVersions';
import { getProtectedDevices } from 'apis/swManagerApis/GetProtectedDevices';
import { getFactoryProtectedDevices } from 'apis/swManagerApis/GetFactoryProtectedDevices';
import { checkSwStatus, searchDeviceVersion } from 'apis/SearchDevices';
import CommissionDeviceQuery from '../CommissionDeviceQuery/CommissionDeviceQuery';
import { doTerminateCommissionOp, removeActionFromActionList } from 'redux/actions';
import './CommissionAddDevicesScreen.css';
import { isSwUpdateRunning, sortBundleMember, SCREEN_INDEXES } from '../utils';
import { getCommissioningStatus } from 'apis/commissioningApis/GetCommissioningStatus';
import CommissionErrorBox from '../CommissionErrorBox/CommissionErrorBox';

const semver = require('semver');

const COLUMNS_INDEXES = {
  0: 'devices',
  1: 'active version',
  2: 'type',
  3: 'sw status'
};

const BUNDLED_DEVICE_NOTE_MESSAGE = 'This device has been commissioned with the one you selected.';

const STATUS_CHECK_INTERVAL_MS = 1 * 60 * 1000; // 1 min
const MIN_IBMS_API_VERSION = 'v6';
// do not let users proceed with following messages
const SW_STATUS_TERMINAL_MESSAGE = 'Update required';
// query every interval time for status update
const SW_STATUS_QUERY_MESSAGE = 'Update pending';
const REQUEST_TIMEOUT_SEC = 10 * 1000; // 10 sec

const CommissionAddDevicesScreen = (props) => {
  const [deviceIds, setDeviceIds] = useState(['', '', '']);
  const [errors, setErrors] = useState(['Device ID cannot be empty.', 'Device ID cannot be empty.', '']);
  const [devicesData, setDevicesData] = useState([{}, {}, {}]);
  const [bundleMembers, setBundleMembers] = useState([]);
  const [currBundleId, setCurrBundleId] = useState('');
  const [lastQuery, setLastQuery] = useState('');
  // determine if the 3rd input is ready only when currenly bundle has only 2 devices
  const [su2ReadOnly, setSu2ReadOnly] = useState(false);

  const [timeoutId, setTimeoutId] = useState('');
  const [isTimeout, setIsTimeout] = useState(false);
  const [defaultVersions, setDefaultVersions] = useState([]);
  const [defaultVerionsRequestStatus, setDefaultVerionsRequestStatus] = useState(Status.NONE);
  const [protectedDevices, setProtectedDevices] = useState([]);
  const [protectedDevicesRequestStatus, setProtectedDevicesRequestStatus] = useState(Status.NONE);
  const [factoryProtectedDevices, setFactoryProtectedDevices] = useState([]);
  const [factoryProtectedDevicesRequestStatus, setFactoryProtectedDevicesRequestStatus] = useState(Status.NONE);
  const [getCommStatusErrorCode, setGetCommStatusErrorCode] = useState(null);

  const [statusTableReady, setStatusTableReady] = useState(false);
  const [initialSwUpdateCheckDone, setInitialSwUpdateCheckDone] = useState(0);
  const [queryIntervalIds, setQueryIntervalIds] = useState(['', '', '']);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const [activeCommIds, setActiveCommIds] = useState([]);
  const activeActions = useSelector((store) => store.data?.actionsList?.active);
  const dispatch = useDispatch();

  const errorsRef = useRef(errors);
  const devicesDataRef = useRef(devicesData);
  const queryIntervalIdsRef = useRef(queryIntervalIds);
  const timeoutRef = useRef(timeoutId);

  useEffect(() => {
    // for re-commissioning to collect any active commissioning operations
    if (currBundleId && activeActions && !isObjectEmpty(activeActions)) {
      const curActiveCommIds = findActiveCommIds();
      setActiveCommIds(curActiveCommIds);
    }
    // reset activeCommIds state in case the user reenters devices that are not bundled
    if (!currBundleId) {
      setActiveCommIds([]);
    }
  }, [currBundleId]);

  // from pivot overview
  useEffect(() => {
    if (props.targetDonor) {
      const newDeviceIds = JSON.parse(JSON.stringify(deviceIds));
      newDeviceIds[0] = props.targetDonor.deviceId;
      setDeviceIds(newDeviceIds);
    }
  }, [props.targetDonor, props.targetServices]);

  // pick up latest state for lexical environment
  useEffect(() => {
    errorsRef.current = errors;
    devicesDataRef.current = devicesData;
    queryIntervalIdsRef.current = queryIntervalIds;
    timeoutRef.current = timeoutId;
  });

  useEffect(() => {
    return () => {
      queryIntervalIdsRef.current.forEach((id) => clearInterval(id));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // get default versions, protected lists during initial load
  useEffect(() => {
    if (props.twoHop) {
      return;
    }
    setDefaultVerionsRequestStatus(Status.PENDING);
    setProtectedDevicesRequestStatus(Status.PENDING);
    setFactoryProtectedDevicesRequestStatus(Status.PENDING);
    const requestTimeoutId = setTimeout(() => {
      setIsTimeout(true);
    }, REQUEST_TIMEOUT_SEC);
    setTimeoutId(requestTimeoutId);

    getDefaultSwVersions()
      .then((res) => {
        if (!res.some((value) => value.deviceType === 'Pivot5G')) {
          setDefaultVerionsRequestStatus(Status.FAILURE);
        } else {
          setDefaultVersions(res);
          setDefaultVerionsRequestStatus(Status.SUCCESS);
        }
      })
      .catch((e) => setDefaultVerionsRequestStatus(Status.FAILURE));

    getProtectedDevices()
      .then((res) => {
        setProtectedDevicesRequestStatus(Status.SUCCESS);

        const translatedRes = Array.isArray(res) ? res : res.values;
        setProtectedDevices(translatedRes || []);
      })
      .catch((e) => setProtectedDevicesRequestStatus(Status.FAILURE));

    getFactoryProtectedDevices()
      .then((res) => {
        setFactoryProtectedDevicesRequestStatus(Status.SUCCESS);
        const translatedRes = Array.isArray(res) ? res : res.values;
        setFactoryProtectedDevices(translatedRes || []);
      })
      .catch((e) => setFactoryProtectedDevicesRequestStatus(Status.FAILURE));
  }, []);

  // for 2hop
  useEffect(() => {
    if (props.twoHop) setDefaultVersions(props.defaultVersions);
  }, [props.defaultVersions]);

  useEffect(() => {
    if (props.twoHop) setProtectedDevices(props.protectedDevices);
  }, [props.protectedDevices]);

  useEffect(() => {
    if (props.twoHop) setFactoryProtectedDevices(props.factoryProtectedDevices);
  }, [props.factoryProtectedDevices]);

  // set status when there's no error
  useEffect(() => {
    if (props.twoHop) {
      props.resetError();
    }

    if (errors.some((e) => e !== '' && e !== BUNDLED_DEVICE_NOTE_MESSAGE)) {
      if (props.twoHop) props.updateError(true);
      setStatusTableReady(false);
      setInitialSwUpdateCheckDone(0);
      setCurrBundleId('');
      props.setRecommBundleId(null);
      setBundleMembers([]);
      setLastQuery('');
      setSu2ReadOnly(false);
    } else {
      setDevicesData((devicesData) =>
        devicesData.map((d, i) => {
          if (Object.keys(d).length === 0 || (i === 2 && deviceIds[2] === '')) {
            return {};
          }

          return {
            ...d,
            swStatus: getSwStatus(d)
          };
        })
      );
      setStatusTableReady(true);
      if (props.twoHop) props.updateError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, deviceIds]);

  useEffect(() => {
    if (bundleMembers.length === 0 || !currBundleId || !lastQuery) return;
    // update ids and devicesData
    let updatedDeviceIds = ['', '', ''];
    let updatedDevicesData = [{}, {}, {}];
    // update bundle notes
    let updatedErrors = ['', '', ''];
    bundleMembers.forEach((member, index) => {
      // updatedDeviceIds[index] = member.deviceId;
      updatedDevicesData[index] = { ...member };
    });
    // sort by product type, DU > HBF_SERVICE > others
    const updatedDevicesDataSorted = updatedDevicesData.sort(sortBundleMember);

    updatedDevicesDataSorted.forEach((device, index) => {
      if (Object.keys(device).length !== 0) {
        if (device.deviceId !== lastQuery) {
          updatedErrors[index] = BUNDLED_DEVICE_NOTE_MESSAGE;
        }
        updatedDeviceIds[index] = device.deviceId;
      }
    });
    setDeviceIds(updatedDeviceIds);
    if (props.twoHop) {
      props.updateDeviceIds(updatedDeviceIds);
    }
    setDevicesData(updatedDevicesDataSorted);
    setErrors(updatedErrors);
    setSu2ReadOnly(Object.keys(updatedDevicesData[2]).length === 0);
  }, [currBundleId, bundleMembers, lastQuery]);

  // check sw update status initially and periodically if update is still running
  useEffect(() => {
    queryIntervalIdsRef.current.forEach((id) => clearInterval(id));
    setQueryIntervalIds(['', '', '']);
    if (!statusTableReady) return;
    devicesDataRef.current.forEach((device, index) => {
      if (device.swStatus === SW_STATUS_QUERY_MESSAGE) {
        checkSwStatus(devicesDataRef.current[index].swUpdateStatusUri).then((status) => {
          const deviceSwUpdateRunning = isSwUpdateRunning(status, device.deviceId);
          // initial check of sw update status
          if (deviceSwUpdateRunning === false) {
            setTimeout(() => {
              searchDeviceVersion(device.deviceId)
                .then((currDevice) => {
                  setDevicesData((devicesData) => [
                    ...devicesData.slice(0, index),
                    {
                      ...devicesData[index],
                      swStatus: getSwStatus(currDevice),
                      swUpdateStatusUri: ''
                    },
                    ...devicesData.slice(index + 1)
                  ]);
                  setErrors((errors) => [
                    ...errors.slice(0, index),
                    getErrorMessage(currDevice),
                    ...errors.slice(index + 1)
                  ]);
                })
                .catch((error) => console.error(error))
                .finally(() => setInitialSwUpdateCheckDone((count) => count + 1));
            }, 500);
          } else {
            setInitialSwUpdateCheckDone((count) => count + 1);
            // timer to constantly check status
            const intervalId = setInterval(() => {
              checkSwStatus(devicesDataRef.current[index].swUpdateStatusUri).then((status) => {
                const deviceSwUpdateRunning = isSwUpdateRunning(status, device.deviceId);
                if (deviceSwUpdateRunning === false) {
                  clearInterval(intervalId);
                  setQueryIntervalIds((queryIntervalIds) => [
                    ...queryIntervalIds.slice(0, index),
                    '',
                    ...queryIntervalIds.slice(index + 1)
                  ]);
                  searchDeviceVersion(device.deviceId)
                    .then((currDevice) => {
                      setDevicesData((devicesData) => [
                        ...devicesData.slice(0, index),
                        { ...devicesData[index], swStatus: getSwStatus(currDevice) },
                        ...devicesData.slice(index + 1)
                      ]);
                      setErrors((errors) => [
                        ...errors.slice(0, index),
                        getErrorMessage(currDevice),
                        ...errors.slice(index + 1)
                      ]);
                    })
                    .catch((error) => console.error(error));

                  return;
                }
                if (queryIntervalIdsRef.current[index] === '') {
                  setQueryIntervalIds((queryIntervalIds) => [
                    ...queryIntervalIds.slice(0, index),
                    intervalId,
                    ...queryIntervalIds.slice(index + 1)
                  ]);
                }
              });
            }, STATUS_CHECK_INTERVAL_MS);
          }
        });
      } else {
        setInitialSwUpdateCheckDone((count) => count + 1);
      }
    });
  }, [statusTableReady, deviceIds]);

  const getErrorMessage = (device) => {
    let errorMessage = '';
    if (device.connectionState !== 'Connected') {
      errorMessage =
        'This device is currently offline. Please wait till it is back online and select this device again.';
    } else if (device.adminState !== 'unlocked') {
      errorMessage = 'Device service state unavailable. Unable to proceed.';
    } else if (device.hasCriticalAlarms) {
      errorMessage = 'Critical alarm exists for this device. Unable to proceed.';
    }

    return errorMessage;
  };

  const onClose = () => {
    props.onClose();
  };

  const onNext = () => {
    if (errors.some((e) => e === BUNDLED_DEVICE_NOTE_MESSAGE) || props.fromPivotOverView) {
      setShowConfirmation(true);
      return;
    }
    props.onNext(SCREEN_INDEXES.ADD_DEVICES, devicesData);
  };

  const isDeviceProtected = (deviceId) => {
    let isProtected = false;
    protectedDevices.forEach((d) => {
      const currId = d.deviceId;
      if (currId === deviceId) {
        isProtected = true;
      }
    });
    factoryProtectedDevices.forEach((d) => {
      const currId = d.deviceId;
      if (currId === deviceId) {
        isProtected = true;
      }
    });

    return isProtected;
  };

  const meetIbmsApiVersionRequirement = (rawVersion) => {
    if (!rawVersion) return false;
    const cleanedRawVersion = semver.valid(semver.coerce(rawVersion));
    if (!cleanedRawVersion) return false;

    const cleanedMinVersion = semver.valid(semver.coerce(MIN_IBMS_API_VERSION));

    return cleanedRawVersion === cleanedMinVersion || semver.gt(cleanedRawVersion, cleanedMinVersion);
  };

  const matchDefaultSwVersion = (rawVersion) => {
    const defaultVersion = defaultVersions.find((v) => v.deviceType === 'Pivot5G').defaultSWVersion;
    return semver.valid(semver.coerce(rawVersion)) === semver.valid(semver.coerce(defaultVersion));
  };

  const getSwStatus = (device) => {
    if (!device) {
      return '';
    }

    if (device.swUpdateStatusUri) {
      return SW_STATUS_QUERY_MESSAGE;
    }

    if (defaultVersions.length === 0) {
      return '';
    }

    if (isDeviceProtected(device.deviceId)) {
      if (meetIbmsApiVersionRequirement(device.ibmsApiVersion)) {
        return '';
      }
      console.log('device is protected and does not meet IBMS version ', device.deviceId);
      return SW_STATUS_TERMINAL_MESSAGE;
    } else {
      if (meetIbmsApiVersionRequirement(device.ibmsApiVersion) && matchDefaultSwVersion(device.softwareVersion)) {
        return '';
      }
      console.log('device does not meet default sw version or min IBMS version', device.deviceId);
      // terminal since we do not have sw update status uri
      return SW_STATUS_TERMINAL_MESSAGE;
    }
  };

  const updateBundleMembers = (devices) => {
    const query = devices[0].deviceId;
    const members = devices[0].members || [];
    const bundleId = devices[0].bundleId;
    setBundleMembers([...members]);
    if (bundleId !== currBundleId) {
      setCurrBundleId(bundleId);
      props.setRecommBundleId(bundleId);
      setLastQuery(query);
    }
  };

  const updateDevicesData = (deviceData, index) => {
    setDevicesData((devicesData) => [
      ...devicesData.slice(0, index),
      { ...deviceData },
      ...devicesData.slice(index + 1)
    ]);
  };

  const errorOnChange = (errorMessage, index) => {
    setErrors((errors) => [...errors.slice(0, index), errorMessage, ...errors.slice(index + 1)]);
  };

  /**
   * update input value and potential error messages
   * @param {string} deviceIdData
   */
  const inputOnChange = (deviceIdData, index) => {
    setDeviceIds((deviceIds) => [...deviceIds.slice(0, index), deviceIdData, ...deviceIds.slice(index + 1)]);
    if (props.twoHop) {
      props.updateDeviceIds([...deviceIds.slice(0, index), deviceIdData, ...deviceIds.slice(index + 1)]);
    }
  };

  const getColumns = () => [0, 1, 2, 3].map((colIndex) => getSingleColumn(colIndex));

  const getTableData = (index, device) => {
    const translatedType = productTypeStringToDisplayStringForSwUpdate(device.productType);
    let displayValue;
    switch (index) {
      case 0:
        return (
          <div className="Commission_AddDevice_DeviceIdContainer">
            <div className="Commission_AddDevice_DeviceHeader_Icon">
              <SvgAF
                img={translatedType === 'Pivot 5G Donor' ? IconLoader.icon_donor : IconLoader.icon_service}
                hover={translatedType === 'Pivot 5G Donor' ? IconLoader.icon_donor : IconLoader.icon_service}
              />
            </div>
            <div className="Commission_AddDevice_DeviceIdText" data-tip={device.deviceId}>
              {device.deviceId}
            </div>
          </div>
        );
      case 1:
        displayValue = device.softwareVersion || 'unknown';
        return (
          <div className="Commission_AddDevice_TableText" data-tip={displayValue}>
            {displayValue}
          </div>
        );
      case 2:
        displayValue = device.productType;
        return (
          <div className="Commission_AddDevice_TableText" data-tip={displayValue}>
            {displayValue}
          </div>
        );
      case 3:
        displayValue = device.swStatus;
        const ibmsVersionToolTipMessage =
          'Device is running default SW version, but this version does not support commissioning.';
        return (
          <div
            className="Commission_AddDevice_TableText"
            data-tip={
              matchDefaultSwVersion(device.softwareVersion) && !meetIbmsApiVersionRequirement(device.ibmsApiVersion)
                ? ibmsVersionToolTipMessage
                : displayValue
            }
          >
            {displayValue}
          </div>
        );
      default:
        break;
    }
  };

  const getSingleColumn = (index) => ({
    Header: (col) => {
      return (
        <div className="ConfigHistoryTable_Header_OuterDiv">
          <span className="ConfigHistoryTable_Header_Label">{COLUMNS_INDEXES[index]}</span>
          {index !== 3 && (
            <div className="ConfigHistoryTableHeader_ResizerSliderIcon">
              <div className="ConfigHistoryTableHeader_ResizerSliderIcon_Bar"></div>
            </div>
          )}
        </div>
      );
    },
    id: `create_site_${COLUMNS_INDEXES[index]}`,
    resizable: true,
    minWidth: 100,
    accessor: (device) => device,
    Cell: (row) => {
      return <div className="ConfigHistoryTable_cellWrapper">{getTableData(index, row.value)}</div>;
    },
    fixed: 'left'
  });

  const getTrimmedDevices = () => {
    let trimmedDevices = [];
    devicesData.forEach((device) => {
      if (Object.keys(device).length > 0) {
        trimmedDevices.push(device);
      }
    });

    return trimmedDevices;
  };

  const getApiRequestsStatus = () => {
    if (isTimeout) return Status.FAILURE;
    const requests = [defaultVerionsRequestStatus, protectedDevicesRequestStatus, factoryProtectedDevicesRequestStatus];
    if (requests.some((r) => r === Status.FAILURE)) {
      if (timeoutId) {
        clearTimeout(timeoutId);
        setTimeoutId('');
      }
      return Status.FAILURE;
    }
    if (requests.some((r) => r === Status.PENDING)) {
      return Status.PENDING;
    }
    if (requests.every((r) => r === Status.SUCCESS)) {
      if (timeoutId) {
        clearTimeout(timeoutId);
        setTimeoutId('');
      }
    }
    return Status.NONE;
  };

  const hasError = () => errors.some((e) => e !== '' && e !== BUNDLED_DEVICE_NOTE_MESSAGE);

  const shouldShowSwTable = () =>
    statusTableReady && devicesData.some((d) => Object.keys(d).length !== 0 && d.swStatus !== '');

  const shouldDisableNextButton = () => {
    if (shouldShowSwTable() || errors.some((e) => e !== '' && e !== BUNDLED_DEVICE_NOTE_MESSAGE)) return true;

    if (errors.some((e) => e === BUNDLED_DEVICE_NOTE_MESSAGE) && !sameBundle()) {
      return true;
    }

    return false;
  };

  const sameBundle = () => {
    const bundleId = devicesData.find((device) => device?.bundle?.bundleId).bundle.bundleId;

    let validMemberCount = 0;
    devicesData.forEach((device) => {
      if (Object.keys(device).length !== 0 && device?.bundle?.bundleId === bundleId) {
        validMemberCount += 1;
      }
    });
    return validMemberCount === bundleMembers.length;
  };

  let overlay = null;
  if (getApiRequestsStatus() === Status.PENDING) {
    overlay = <div className="LoadingCircle_Overlay">{LOADING_CIRCLE}</div>;
  } else if (getApiRequestsStatus() === Status.FAILURE) {
    overlay = (
      <div className="RequestErrorContainer">
        Fail to retrieve required data for commissioning. Please try again later.
      </div>
    );
  }

  let commissionDevices = [];
  if (!props.fromPivotOverView) {
    commissionDevices = ['donor', 'service', 'service'];
  } else {
    commissionDevices = ['donor'];
    for (let i = 0; i < props.targetServices.length; i++) {
      commissionDevices.push('service');
    }
  }

  // poll specific commissioning operation status every 5 seconds until it's either completed or terminated
  const pollCommissioningStatus = async (commissioningId) => {
    const checkCallback = (result) => ['Completed', 'Terminated'].includes(result?.runtimeStatus);
    const poll = createApiPolling(getCommissioningStatus, checkCallback, { delay: 5000, timeoutMinute: 1 });

    try {
      await poll(commissioningId);
      dispatch(removeActionFromActionList(commissioningId, 'active'));
    } catch (error) {
      setGetCommStatusErrorCode(error.message);
    }
  };

  // helper method to find all active commIds that are associated with curBundleId/DU Id
  const findActiveCommIds = () => {
    const activeCommIds = [];
    !isObjectEmpty(activeActions) &&
      Object.keys(activeActions).forEach((actionId) => {
        let bundleId = activeActions[actionId]?.input?.bundleId;
        let donorId = activeActions[actionId]?.input?.members?.donor;
        if ((bundleId && bundleId === currBundleId) || (deviceIds && donorId && donorId === deviceIds[0])) {
          activeCommIds.push(activeActions[actionId]?.input?.commissioningId);
        }
      });
    return activeCommIds;
  };

  // Proceed to next screen when Proceed button is clicked and there is no active commOp
  useEffect(() => {
    const curActiveCommIds = findActiveCommIds();

    if (actionPending && curActiveCommIds?.length === 0 && getCommStatusErrorCode == null) {
      setActionPending(false);
      props.onNext(SCREEN_INDEXES.ADD_DEVICES, devicesData);
    }
  }, [actionPending, activeActions, getCommStatusErrorCode]);

  const handleTerminateCommOps = () => {
    setActionPending(true);
    if (errors.some((e) => e === BUNDLED_DEVICE_NOTE_MESSAGE) && activeCommIds.length > 0) {
      for (let commissioningId of activeCommIds) {
        const cb_success = () => {
          pollCommissioningStatus(commissioningId);
        };
        const cb_failed = (error) => {
          console.error(error);
          props.setTerminateCommissionOpErrorCode(error.message);
        };
        commissioningId && dispatch(doTerminateCommissionOp(commissioningId, cb_success, cb_failed));
      }
    }
  };

  let getCommStatusErrorBox = null;
  if (getCommStatusErrorCode) {
    getCommStatusErrorBox = (
      <CommissionErrorBox
        title={`Get Commissioning Status Error: ${getCommStatusErrorCode}`}
        message={'Something went wrong. Please try again later.'}
        onClose={props.onClose}
      />
    );
  }

  let terminateCommissioningErrorBox = null;
  if (props.terminateCommissionOpErrorCode) {
    terminateCommissioningErrorBox = (
      <CommissionErrorBox
        title={`Terminate CommissionOp Error: ${props.terminateCommissionOpErrorCode}`}
        message={'Something went wrong. Please try again later.'}
        onClose={props.onClose}
      />
    );
  }

  return (
    <div
      className={classnames({
        Commission_AddDeviceContainer: !props.fromPivotOverView,
        ReCommission_AddDeviceContainer: props.fromPivotOverView
      })}
    >
      {overlay}
      {showConfirmation && (
        <Modal animateX={false} close={() => setShowConfirmation(false)} noPadding height={226} width={480}>
          {getCommStatusErrorCode && getCommStatusErrorBox}
          {props.terminateCommissionOpErrorCode && terminateCommissioningErrorBox}
          <div className="Commission_AddDevice_ConfirmationContainer">
            <div className="Commission_AddDevice_ConfirmationText">
              The devices will be recommissioned. All of the associated active commissioning instances will be
              terminated.
            </div>
            <div className="Commission_AddDevice_ConfirmationBottomContainer">
              <button
                className="Commission_AddDevice_Confirmation_Bottom_CancelButton"
                disabled={actionPending}
                onClick={() => setShowConfirmation(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={classnames({
                  Commission_AddDevice_Confirmation_Bottom_ProceedButton: true
                })}
                onClick={() => {
                  handleTerminateCommOps();
                }}
              >
                {actionPending ? BUTTON_LOADING_CIRCLE : 'Proceed'}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {!props.twoHop && <div className={'Commission_AddDevice_Header'}>Create a Pivot</div>}
      <div className={'Commission_AddDevice_AddDevicesContainer'}>
        <div className={'Commission_AddDevice_SiteSubheader'}>{props.title ?? 'Add devices to this Pivot'}</div>
        <div className={'Commission_AddDevice_SiteSubheaderMessage'}>
          {props.subtitle ?? 'Pivots should have one Donor Unit and up to two Service Units.'}
        </div>
        {commissionDevices &&
          commissionDevices.map((productType, index) => (
            <div
              className={classnames({
                Commission_AddDevice_DeviceContainer: !props.fromPivotOverView,
                ReCommission_AddDevice_DeviceContainer: props.fromPivotOverView
              })}
              key={index}
            >
              <div
                className={classnames({
                  Commission_AddDevice_DeviceHeaderContainer: !props.fromPivotOverView,
                  ReCommission_AddDevice_DeviceHeaderContainer: props.fromPivotOverView
                })}
              >
                <div className="Commission_AddDevice_DeviceHeader_Icon">
                  <SvgAF
                    img={productType === 'donor' ? IconLoader.icon_donor : IconLoader.icon_service}
                    hover={productType === 'donor' ? IconLoader.icon_donor : IconLoader.icon_service}
                  />
                </div>
                <div className="Commission_AddDevice_DeviceTypeText">
                  {productType[0].toUpperCase() +
                    productType.slice(1) +
                    (!props.fromPivotOverView ? ' device ID/name' : '')}
                </div>
                {(index === 0 || index === 1) && !props.fromPivotOverView && (
                  <div className="Commission_AddDevice_DeviceTypeText_Required">{'(Required)'}</div>
                )}
              </div>

              <CommissionDeviceQuery
                fromPivotOverView={props.fromPivotOverView}
                key={index}
                deviceIndex={index}
                productType={productType}
                isBundled={devicesData[index]?.isBundled}
                devicesData={devicesData}
                deviceIds={deviceIds}
                errors={errors}
                inputOnChange={inputOnChange}
                errorOnChange={errorOnChange}
                updateDevicesData={updateDevicesData}
                updateBundleMembers={updateBundleMembers}
                readOnly={index === 2 && su2ReadOnly === true}
                setRecommBundleId={props.setRecommBundleId}
              />
            </div>
          ))}
      </div>
      {shouldShowSwTable() && initialSwUpdateCheckDone >= devicesData.length && (
        <div className="Commission_AddDevice_SwTableContainer">
          <ScrollReactTable
            key={1}
            showPagination={false}
            resizable={true}
            sortable={false}
            minRows={2}
            noDataText={''}
            fetching={false}
            className={'-highlight configHistory_Table Global_Table'}
            defaultPageSize={0}
            pageSize={3}
            getTdProps={(state, rowInfo, column, instance) => {
              return {
                onClick: (e, handleOriginal) => {
                  if (handleOriginal) {
                    handleOriginal(); /* Suggested by documentation, makes sure that default click functions still get called if necessary*/
                  }
                }
              };
            }}
            data={getTrimmedDevices(devicesData)}
            columns={[...getColumns()]}
            withFixedColumns={true}
          />
        </div>
      )}
      {!props.twoHop && (
        <div className={'Commission_AddDevice_BottomContainer'}>
          <button className="Commission_AddDevice_Bottom_CancelButton" onClick={onClose}>
            Cancel
          </button>
          {!hasError() && devicesData.some((device) => device.swStatus === SW_STATUS_TERMINAL_MESSAGE) ? (
            <button
              type="submit"
              className={classnames({
                Commission_AddDevice_Bottom_NextButton: true
              })}
              onClick={props.onClose}
            >
              Close
            </button>
          ) : (
            <button
              type="submit"
              className={classnames({
                Commission_AddDevice_Bottom_NextButton: true,
                Commission_AddDevice_Bottom_NextButton_Disabled: shouldDisableNextButton()
              })}
              onClick={onNext}
            >
              Next
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CommissionAddDevicesScreen;
